/** The risk view's reducers: control state, latch display, and why an action is blocked (U046-U050, UX08). */
import type { ControlRow, LatchRow, LimitRow } from "../../contracts/account.ts";

/** The first control of a kind, if the page holds one. */
export function controlOf(
  rows: readonly ControlRow[],
  kind: ControlRow["kind"],
): ControlRow | undefined {
  return rows.find((row) => row.kind === kind);
}

/** Whether new entries are paused (a missing entries control is not read as enabled). */
export function entriesState(rows: readonly ControlRow[]): ControlRow["state"] | "UNKNOWN" {
  return controlOf(rows, "ENTRIES")?.state ?? "UNKNOWN";
}

export interface LatchView {
  readonly row: LatchRow;
  /** Acknowledged by the server's record or by this browser; a display flag that releases nothing. */
  readonly acknowledged: boolean;
  /** The latch exactly as the server says: acknowledgement never changes it. */
  readonly held: boolean;
  readonly text: string;
}

/** The key under which a latch's local acknowledgement is kept (apart from incident ids). */
export function ackKey(latchId: string): string {
  return `latch:${latchId}`;
}

/** A latch as displayed: acknowledged or not, held exactly as the server reports (UX08). */
export function latchView(row: LatchRow, local: ReadonlySet<string>): LatchView {
  const acknowledged = row.acknowledged || local.has(ackKey(row.latchId));
  const held = row.state === "ENGAGED";
  const text = held
    ? acknowledged
      ? `Alert acknowledged (display only). The latch is still ENGAGED; ${row.releaseRequires} releases it.`
      : `ENGAGED. ${row.releaseRequires} releases it; acknowledging the alert does not.`
    : "RELEASED by its named command or by the controller.";
  return { row, acknowledged, held, text };
}

/** Every reason an action is blocked now, each with its source (U050). */
export function blockedBecause(
  controls: readonly ControlRow[],
  latches: readonly LatchRow[],
  limits: readonly LimitRow[],
): readonly string[] {
  const reasons: string[] = [];
  for (const control of controls) {
    for (const blocker of control.blockers) {
      reasons.push(`${control.label}: ${blocker}.`);
    }
  }
  for (const latch of latches) {
    if (latch.state === "ENGAGED") {
      reasons.push(`${latch.label} is ENGAGED: ${latch.reason}.`);
    }
  }
  for (const limit of limits) {
    if (limit.blocking) {
      reasons.push(`${limit.label} blocks new entries: ${limit.explanation}`);
    }
  }
  return reasons;
}
